import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X, GitMerge, Loader2, AlertTriangle, Search as SearchIcon, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../../lib/supabase';
import { CustomerTypeahead, type CustomerSearchResult } from '../Workshop/components/CustomerTypeahead';
import { errorMessage } from '../../../lib/errors';

interface MergeCustomerModalProps {
    isOpen: boolean;
    onClose: () => void;
    orgId: string;
    currentCustomer: { id: string; full_name: string; phone: string | null; email: string | null };
    /** Called after a successful merge with the id of the customer that was kept. */
    onMerged: (keptId: string) => void;
}

interface Party { id: string; full_name: string; phone: string | null; email: string | null }

export default function MergeCustomerModal({ isOpen, onClose, orgId, currentCustomer, onMerged }: MergeCustomerModalProps) {
    const [other, setOther] = useState<Party | null>(null);
    const [keepId, setKeepId] = useState(currentCustomer.id);
    const [status, setStatus] = useState<'idle' | 'merging'>('idle');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            setOther(null);
            setKeepId(currentCustomer.id);
            setStatus('idle');
            setError(null);
        }
    }, [isOpen, currentCustomer.id]);

    if (!isOpen) return null;

    const handleSelect = (c: CustomerSearchResult) => {
        if (c.id === currentCustomer.id) {
            setError('That is the same customer — pick a different one to merge in.');
            return;
        }
        setError(null);
        setOther({ id: c.id, full_name: c.full_name, phone: c.phone, email: c.email });
        setKeepId(currentCustomer.id);
    };

    const parties: Party[] = other ? [currentCustomer, other] : [currentCustomer];
    const mergeId = other ? (keepId === currentCustomer.id ? other.id : currentCustomer.id) : null;
    const keepParty = parties.find((p) => p.id === keepId) || currentCustomer;
    const dropParty = parties.find((p) => p.id === mergeId) || null;

    const handleMerge = async () => {
        if (!other || !mergeId) return;
        setStatus('merging');
        setError(null);
        try {
            const { error: rpcError } = await supabase.rpc('fn_merge_customers', {
                p_org_id: orgId,
                p_keep_id: keepId,
                p_merge_id: mergeId,
            });
            if (rpcError) throw rpcError;
            toast.success('Customers merged');
            onMerged(keepId);
        } catch (e) {
            setError(errorMessage(e) || 'Failed to merge customers');
            setStatus('idle');
        }
    };

    const Card = ({ p, role }: { p: Party; role: 'keep' | 'drop' }) => (
        <div className={`flex-1 rounded-lg border p-3 ${role === 'keep' ? 'border-success/40 bg-success/5' : 'border-danger/30 bg-danger/5'}`}>
            <div className="flex items-center justify-between">
                <span className={`text-[10px] font-bold uppercase tracking-wider ${role === 'keep' ? 'text-success' : 'text-danger'}`}>
                    {role === 'keep' ? 'Keep' : 'Remove'}
                </span>
            </div>
            <p className="text-sm font-bold text-fg mt-1 truncate">{p.full_name || 'Unnamed'}</p>
            <p className="text-xs text-fg-3 truncate">{p.phone || '—'}</p>
            <p className="text-xs text-fg-3 truncate">{p.email || '—'}</p>
        </div>
    );

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-scrim/80 backdrop-blur-sm animate-in fade-in">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-lg bg-panel border border-line rounded-xl shadow-2xl overflow-hidden"
            >
                <div className="flex items-center justify-between p-6 border-b border-tint/10">
                    <h2 className="text-xl font-bold text-fg flex items-center gap-2">
                        <GitMerge className="w-5 h-5 text-accent" />
                        Merge duplicate customer
                    </h2>
                    <button
                        onClick={onClose}
                        disabled={status === 'merging'}
                        className="p-2 rounded-full hover:bg-tint/10 transition-colors text-fg-3 hover:text-fg disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    <div>
                        <p className="text-sm text-fg-2 mb-1">
                            Combine <strong className="text-fg">{currentCustomer.full_name}</strong> with another record that's the same person.
                        </p>
                        <label className="text-xs font-bold text-fg-4 uppercase flex items-center gap-1.5 mb-2 mt-3">
                            <SearchIcon className="w-3 h-3" /> Find the duplicate
                        </label>
                        <CustomerTypeahead onSelect={handleSelect} placeholder="Search the other customer record…" />
                    </div>

                    {other && (
                        <>
                            <div className="flex items-center gap-2">
                                <Card p={keepParty} role="keep" />
                                <ArrowRight className="w-4 h-4 text-fg-4 shrink-0" />
                                {dropParty && <Card p={dropParty} role="drop" />}
                            </div>

                            <button
                                type="button"
                                onClick={() => setKeepId((id) => (id === currentCustomer.id ? other.id : currentCustomer.id))}
                                className="text-xs font-semibold text-accent hover:underline"
                            >
                                Swap — keep {keepId === currentCustomer.id ? other.full_name : currentCustomer.full_name} instead
                            </button>

                            <div className="flex items-start gap-2 p-3 rounded-lg bg-gold-fill/10 border border-gold/20 text-gold text-xs">
                                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                <span>
                                    All vehicles, jobs, invoices and service history from <strong>{dropParty?.full_name}</strong> move onto{' '}
                                    <strong>{keepParty.full_name}</strong>, and the duplicate record is deleted. This can't be undone.
                                </span>
                            </div>
                        </>
                    )}

                    {error && (
                        <div className="p-3 bg-danger/10 border border-danger/20 rounded-lg flex items-center gap-2 text-danger text-sm">
                            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={status === 'merging'}
                            className="flex-1 px-4 py-2.5 rounded-lg font-bold text-fg-2 hover:bg-tint/5 border border-tint/10 transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleMerge}
                            disabled={!other || status === 'merging'}
                            className="flex-1 font-bold py-2.5 rounded-lg transition-all flex items-center justify-center gap-2 bg-danger-fill text-on-fill hover:bg-danger-fill disabled:opacity-50"
                        >
                            {status === 'merging' ? <><Loader2 className="w-5 h-5 animate-spin" />Merging…</> : <><GitMerge className="w-5 h-5" />Merge &amp; delete duplicate</>}
                        </button>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
